import { DepartmentStatus, DepartmentType } from '@21k/shared';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createDepartment,
  getApiErrorMessage,
  getDepartment,
  listDepartments,
  listEscalationTargets,
  addEscalationTarget,
  removeEscalationTarget,
  updateDepartment,
} from '../../api/client';
import type { DepartmentDetail, EscalationTargetSummary } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditDepartments, canWriteDepartments } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

const DEPARTMENT_TYPES = Object.values(DepartmentType);

export default function DepartmentFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [detail, setDetail] = useState<DepartmentDetail | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<DepartmentType>(DepartmentType.INTERFACE);
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<DepartmentStatus>(DepartmentStatus.ACTIVE);
  const [erpReferenceId, setErpReferenceId] = useState('');
  const [erpReferenceType, setErpReferenceType] = useState<'school' | 'program' | ''>('');
  const [canHandleParentReplies, setCanHandleParentReplies] = useState(false);
  const [escalationTargets, setEscalationTargets] = useState<EscalationTargetSummary[]>([]);
  const [allDepartments, setAllDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [newTargetDeptId, setNewTargetDeptId] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = currentUser && (isNew ? canWriteDepartments(currentUser) : canEditDepartments(currentUser));
  const showErpRef = type === DepartmentType.SCHOOL || type === DepartmentType.PROGRAM;

  useEffect(() => {
    if (isNew || !id) return;
    getDepartment(id)
      .then((d) => {
        setDetail(d);
        setName(d.name);
        setType(d.type);
        setDescription(d.description ?? '');
        setStatus(d.status);
        setErpReferenceId(d.erpReferenceId ?? '');
        setErpReferenceType((d.erpReferenceType as 'school' | 'program') ?? '');
        setCanHandleParentReplies(d.canHandleParentReplies ?? false);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    if (isNew || !id) return;
    void listEscalationTargets(id).then(setEscalationTargets).catch(() => undefined);
    void listDepartments().then((depts) => setAllDepartments(depts)).catch(() => undefined);
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      const erpRef = erpReferenceId.trim() || undefined;
      const erpType = erpReferenceType || undefined;

      if (isNew) {
        const created = await createDepartment({
          name: name.trim(),
          type,
          description: description.trim() || undefined,
          erpReferenceId: showErpRef ? erpRef : undefined,
          erpReferenceType: showErpRef ? erpType : undefined,
          canHandleParentReplies,
        });
        navigate(PLATFORM_CONFIG.department(created.id));
      } else if (id) {
        const updated = await updateDepartment(id, {
          name: name.trim(),
          type,
          description: description.trim() || null,
          status,
          erpReferenceId: showErpRef ? erpRef ?? null : null,
          erpReferenceType: showErpRef ? erpType ?? null : null,
          canHandleParentReplies,
        });
        setDetail(updated);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddEscalationTarget() {
    if (!id || !newTargetDeptId) return;
    setSaving(true);
    setError('');
    try {
      await addEscalationTarget(id, { toDepartmentId: newTargetDeptId });
      setEscalationTargets(await listEscalationTargets(id));
      setNewTargetDeptId('');
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveEscalationTarget(targetId: string) {
    if (!id) return;
    setSaving(true);
    setError('');
    try {
      await removeEscalationTarget(id, targetId);
      setEscalationTargets(await listEscalationTargets(id));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading">Loading department…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={PLATFORM_CONFIG.departments} className="page-breadcrumb">
            ← Departments
          </Link>
          <h1>{isNew ? 'New department' : name || 'Department'}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="detail-grid">
        <form className="card card-body" onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="dept-name">Name</label>
            <input
              id="dept-name"
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={!canEdit}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dept-type">Type</label>
              <select
                id="dept-type"
                className="form-control"
                value={type}
                onChange={(e) => setType(e.target.value as DepartmentType)}
                disabled={!canEdit}
              >
                {DEPARTMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {!isNew && (
              <div className="form-group">
                <label htmlFor="dept-status">Status</label>
                <select
                  id="dept-status"
                  className="form-control"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DepartmentStatus)}
                  disabled={!canEdit}
                >
                  <option value={DepartmentStatus.ACTIVE}>ACTIVE</option>
                  <option value={DepartmentStatus.ARCHIVED}>ARCHIVED</option>
                </select>
              </div>
            )}
          </div>

          {showErpRef && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dept-erp-type">ERP reference type</label>
                <select
                  id="dept-erp-type"
                  className="form-control"
                  value={erpReferenceType}
                  onChange={(e) =>
                    setErpReferenceType(e.target.value as 'school' | 'program' | '')
                  }
                  disabled={!canEdit}
                >
                  <option value="">—</option>
                  <option value="school">school</option>
                  <option value="program">program</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="dept-erp-id">ERP reference ID</label>
                <input
                  id="dept-erp-id"
                  className="form-control"
                  value={erpReferenceId}
                  onChange={(e) => setErpReferenceId(e.target.value)}
                  disabled={!canEdit}
                  placeholder="ERP entity ID for routing"
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={canHandleParentReplies}
                onChange={(e) => setCanHandleParentReplies(e.target.checked)}
                disabled={!canEdit}
              />{' '}
              Can handle parent-facing replies
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="dept-desc">Description</label>
            <textarea
              id="dept-desc"
              className="form-control"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
            />
          </div>

          {canEdit && (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create department' : 'Save changes'}
            </button>
          )}
        </form>

        {detail && (
          <div className="card card-body summary-card">
            <h2 className="summary-card-title">Summary</h2>
            <div className="dept-stat">
              <div className="dept-stat-label">Type</div>
              <div className="dept-stat-value">
                <Badge label={detail.type} className="badge-info dept-type-badge" />
              </div>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">Created by</div>
              <div className="dept-stat-value">
                {detail.createdBy.firstName} {detail.createdBy.lastName}
              </div>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">Members</div>
              <div className="dept-stat-value">{detail._count.members}</div>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">Tickets</div>
              <div className="dept-stat-value">{detail._count.tickets}</div>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">SLA rules</div>
              <Link
                className="dept-stat-value link"
                to={`${PLATFORM_CONFIG.sla}?departmentId=${detail.id}`}
              >
                {detail._count.slaRules}
              </Link>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">Channel configs</div>
              <Link className="dept-stat-value link" to={`/channels?departmentId=${detail.id}`}>
                {detail._count.channelConfigs}
              </Link>
            </div>
            <div className="dept-stat">
              <div className="dept-stat-label">Teams</div>
              <Link className="dept-stat-value link" to={PLATFORM_CONFIG.departmentTeams(detail.id)}>
                Manage teams
              </Link>
            </div>
          </div>
        )}
      </div>

      {!isNew && id && canEdit && (
        <div className="card card-body" style={{ marginTop: '1rem' }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1rem' }}>Cross-department escalation targets</h2>
          <p className="text-muted" style={{ marginTop: 0 }}>
            When empty, agents may escalate to any other department. Add targets to restrict choices.
          </p>
          <ul className="plain-list">
            {escalationTargets.map((t) => (
              <li key={t.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span>{t.toDepartment.name}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void handleRemoveEscalationTarget(t.id)}
                  disabled={saving}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div className="form-row" style={{ marginTop: '0.75rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Add target department</label>
              <select
                className="form-control"
                value={newTargetDeptId}
                onChange={(e) => setNewTargetDeptId(e.target.value)}
              >
                <option value="">Select…</option>
                {allDepartments
                  .filter((d) => d.id !== id)
                  .filter((d) => !escalationTargets.some((t) => t.toDepartmentId === d.id))
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: 'flex-end' }}
              disabled={saving || !newTargetDeptId}
              onClick={() => void handleAddEscalationTarget()}
            >
              Add
            </button>
          </div>
        </div>
      )}
    </>
  );
}
